/* eslint-disable react/no-danger */
import React from 'react';
import FullCalendar, {
 EventClickArg,
  EventContentArg,
  FormatterInput
} from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  ATMPopover,
  IORGDataTableQueryState,
} from 'shared-it-appmod-ui';
import { CalendarView } from 'src/constants';
import Moment from 'src/libraries/moment.library';
import {
  getEventContent,
  getEventPopup,
  getMonthViewContent } from 'src/selectors/flights-calendar.selector';

type IProps = {
  calendarEvents: any;
  calendarComponentRef: any;
  parameters: IORGDataTableQueryState;
  handleFetch: (params: IORGDataTableQueryState, view?: CalendarView) => void;
  handleViewMount: (data) => void;
  handleCalendarEvents: (data) => void;
};

const FlightsCalendarView: React.FC<IProps> = ({
  parameters,
  calendarEvents,
  calendarComponentRef,
  handleFetch,
  handleViewMount,
  handleCalendarEvents,
}) => {
  const getApi = () => {
    return calendarComponentRef.current.getApi();
  };

  const getData = () => {
    return getApi().currentDataManager.data;
  };

  const goToDay = (date: Date | null) => {
    window.scrollTo(0, 0);
    getApi().changeView(CalendarView.DayView);
    getApi().gotoDate(date);
  };

  const fetchData = (plus: boolean) => {
    let flightDateStart;
    let flightDateEnd;
    let view;
    switch (getData().currentViewType) {
      case CalendarView.MonthView: {
        view = CalendarView.MonthView;
        if (plus) {
          flightDateStart = Moment.utc(getData().currentDate).add(1, 'month').startOf('month').format('YYYY-MM-DD');
          flightDateEnd = Moment.utc(getData().currentDate).add(1, 'month').endOf('month').format('YYYY-MM-DD');
        } else {
          flightDateStart = Moment.utc(getData().currentDate).subtract(1, 'month').startOf('month').format('YYYY-MM-DD');
          flightDateEnd = Moment.utc(getData().currentDate).subtract(1, 'month').endOf('month').format('YYYY-MM-DD');
        }
        break;
      }
      case CalendarView.WeekView: {
        view = CalendarView.WeekView;
        if (plus) {
          flightDateStart = Moment.utc(getData().currentDate).add(1, 'week').startOf('week').format('YYYY-MM-DD');
          flightDateEnd = Moment.utc(getData().currentDate).add(1, 'week').endOf('week').format('YYYY-MM-DD');
        } else {
          flightDateStart = Moment.utc(getData().currentDate).subtract(1, 'week').startOf('week').format('YYYY-MM-DD');
          flightDateEnd = Moment.utc(getData().currentDate).subtract(1, 'week').endOf('week').format('YYYY-MM-DD');
        }
        break;
      }
      case CalendarView.DayView: {
        view = CalendarView.DayView;
        if (plus) {
          flightDateStart = Moment.utc(getData().currentDate).add(1, 'day').startOf('day').format('YYYY-MM-DD');
          flightDateEnd = Moment.utc(getData().currentDate).add(1, 'day').endOf('day').format('YYYY-MM-DD');
        } else {
          flightDateStart = Moment.utc(getData().currentDate).subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
          flightDateEnd = Moment.utc(getData().currentDate).subtract(1, 'day').endOf('day').format('YYYY-MM-DD');
        }
        break;
      }
    }
    handleFetch({
      ...parameters,
      flightDateStart,
      flightDateEnd,
    }, view);
  };

  const fetchPerView = (view: string) => {
    let flightDateStart;
    let flightDateEnd;
    let calendarView;
    switch (view) {
      case CalendarView.MonthView: {
        calendarView = CalendarView.MonthView;
        flightDateStart = Moment.utc(getData().currentDate).startOf('month').format('YYYY-MM-DD');
        flightDateEnd = Moment.utc(getData().currentDate).endOf('month').format('YYYY-MM-DD');
        break;
      }
      case CalendarView.WeekView: {
        calendarView = CalendarView.WeekView;
        flightDateStart = Moment.utc(getData().currentDate).startOf('week').format('YYYY-MM-DD');
        flightDateEnd = Moment.utc(getData().currentDate).endOf('week').format('YYYY-MM-DD');
        break;
      }
      case CalendarView.DayView: {
        calendarView = CalendarView.DayView;
        flightDateStart = Moment.utc(getData().currentDate).startOf('day').format('YYYY-MM-DD');
        flightDateEnd = Moment.utc(getData().currentDate).endOf('day').format('YYYY-MM-DD');
        break;
      }
      default: {
        calendarView = CalendarView.DayView;
        flightDateStart = Moment.utc().startOf('day').format('YYYY-MM-DD');
        flightDateEnd = Moment.utc().endOf('day').format('YYYY-MM-DD');
        break;
      }
    }
    handleFetch({
      ...parameters,
      flightDateStart,
      flightDateEnd,
    }, calendarView);
  };

  const customButtons = {
    customPrev: {
      icon: 'chevron-left',
      click: () => {
        fetchData(false);
        getApi().prev();
      },
    },
    customNext: {
      icon: 'chevron-right',
      click: () => {
        fetchData(true);
        getApi().next();
      },
    },
    customDay: {
      text: 'Day',
      click: () => {
        fetchPerView(CalendarView.DayView);
        getApi().changeView(CalendarView.DayView);
      },
    },
    customWeek: {
      text: 'Week',
      click: () => {
        fetchPerView(CalendarView.WeekView);
        getApi().changeView(CalendarView.WeekView);
      },
    },
    customMonth: {
      text: 'Month',
      click: () => {
        fetchPerView(CalendarView.MonthView);
        getApi().changeView(CalendarView.MonthView);
      },
    },
    customToday: {
      text: 'Today',
      click: () => {
        fetchPerView('Today');
        getApi().changeView(CalendarView.DayView);
        getApi().today();
      },
    },
  };

  const headerToolBar = {
    left: 'customToday customDay,customWeek,customMonth',
    center: 'title',
    right: 'customPrev,customNext',
  };

  const eventTimeFormat = {
    hour: 'numeric',
    minute: '2-digit',
    omitZeroMinute: true,
    meridiem: 'short'
  } as FormatterInput;

  return (
    <FullCalendar
      selectable
      eventMaxStack={10}
      eventDisplay="block"
      events={calendarEvents}
      slotEventOverlap={false}
      themeSystem="fontawesome"
      ref={calendarComponentRef}
      customButtons={customButtons}
      headerToolbar={headerToolBar}
      timeZone="America/Los_Angeles"
      eventTimeFormat={eventTimeFormat}
      initialView={CalendarView.MonthView}
      dateClick={(info) => goToDay(info.date)}
      scrollTime={Moment.utc().startOf('day').format('HH:mm:ss')}
      viewDidMount={({ view }) => handleViewMount(view.type)}
      plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
      eventClick={(args: EventClickArg) => {
        if (args.view.type === CalendarView.MonthView) {
          goToDay(args.event.start);
        }
        handleCalendarEvents(args);
      }}
      eventContent={(args: EventContentArg) => {
        switch (args.view.type) {
          case CalendarView.DayView: {
            return (
              <ATMPopover
                position="top center"
                trigger={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getEventContent(args).html,
                    }}
                  />
                }
                content={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getEventPopup(args),
                    }}
                  />
                }
              />
            );
          }
          case CalendarView.WeekView: {
            return (
              <ATMPopover
                position="right center"
                trigger={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getEventContent(args).html,
                    }}
                  />
                }
                content={
                  <div
                    dangerouslySetInnerHTML={{
                      __html: getEventPopup(args),
                    }}
                  />
                }
              />
            );
          }
          case CalendarView.MonthView: {
            return getMonthViewContent(args);
          }
          default: {
            return { html: '' };
          }
        }
      }}
    />
  );
};

export default FlightsCalendarView;
